import { useState } from 'react'
import { useParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Spinner, Empty, relTime } from '../../components/ui.jsx'
import { ConfirmarBorrado } from '../../components/ConfirmarBorrado.jsx'
import { useAyuda } from '../../lib/ayuda.jsx'
import { AyudaEnv } from '../../components/AyudaContrato.jsx'

const vacia = { clave: '', valor: '', secreto: true }

export function Env() {
  // La parte del contrato que toca a esta página, en el panel de ayuda («?» de la cabecera).
  useAyuda(() => ({ titulo: 'Variables · el contrato', nodo: <AyudaEnv /> }), [])

  const { slug } = useParams()
  const qc = useQueryClient()
  const [form, setForm] = useState(vacia)
  const [editando, setEditando] = useState(null)
  const [texto, setTexto] = useState('')
  const [importarSecreto, setImportarSecreto] = useState(true)
  const [aBorrar, setABorrar] = useState(null)

  const q = useQuery({ queryKey: ['env', slug], queryFn: () => api.env(slug) })
  const refrescar = () => qc.invalidateQueries({ queryKey: ['env', slug] })

  const guardar = useMutation({
    mutationFn: (v) => api.saveEnv(slug, v),
    onSuccess: () => { setForm(vacia); setEditando(null); refrescar() },
  })
  const borrar = useMutation({
    mutationFn: (clave) => api.deleteEnv(slug, clave),
    onSuccess: () => { setABorrar(null); refrescar() },
  })
  const previsualizar = useMutation({ mutationFn: () => api.previewEnv(slug, texto) })
  const importar = useMutation({
    mutationFn: () => api.importEnv(slug, texto, importarSecreto),
    onSuccess: () => { setTexto(''); previsualizar.reset(); refrescar() },
  })
  const desplegar = useMutation({ mutationFn: () => api.deployEnv(slug) })

  const err = guardar.error ?? borrar.error ?? importar.error ?? previsualizar.error ?? desplegar.error
  const previa = previsualizar.data

  if (q.isPending) return <Spinner />
  if (q.isError) {
    return <div className="alert error">{q.error.body?.detail ?? 'No se pudieron leer las variables.'}</div>
  }

  const enviar = (e) => {
    e.preventDefault()
    if (form.clave && !guardar.isPending) guardar.mutate(form)
  }

  return (
    <>
      <div className="section-head">
        <div>
          <h1 className="h1">Variables de entorno</h1>
          <p className="lede">
            Se inyectan en la aplicación al desplegar. Los cambios necesitan un despliegue para
            surtir efecto.
          </p>
        </div>
        <button className="btn secondary" disabled={desplegar.isPending}
                onClick={() => desplegar.mutate()}>
          {desplegar.isPending ? 'Desplegando…' : 'Aplicar y desplegar'}
        </button>
      </div>

      {desplegar.isSuccess && (
        <div className="alert" style={{ marginBottom: 16 }}>
          Despliegue lanzado con {desplegar.data.aplicadas} variables.
        </div>
      )}
      {err && (
        <div className="alert error" style={{ marginBottom: 16 }}>
          {err.body?.detail ?? err.message}
          {err.body?.error && <> · <code>{err.body.error}</code></>}
        </div>
      )}

      {/* ── una a una ─────────────────────────────────────────────────────── */}
      <form className="card card-pad" onSubmit={enviar} style={{ marginBottom: 20 }}>
        <div className="field-label">{editando ? `Editar ${editando}` : 'Añadir una variable'}</div>
        <div className="toolbar">
          <input className="input" placeholder="NOMBRE" value={form.clave} spellCheck="false"
                 disabled={!!editando} style={{ width: 220, fontFamily: 'var(--mono)' }}
                 onChange={(e) => setForm({ ...form, clave: e.target.value.trim() })} />
          <input className="input" placeholder="valor" value={form.valor} spellCheck="false"
                 style={{ flex: 1, minWidth: 200, fontFamily: 'var(--mono)' }}
                 onChange={(e) => setForm({ ...form, valor: e.target.value })} />
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--fg-2)' }}>
            <input type="checkbox" checked={form.secreto}
                   onChange={(e) => setForm({ ...form, secreto: e.target.checked })} />
            secreto
          </label>
          <button className="btn primary" disabled={!form.clave || guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : editando ? 'Guardar' : 'Añadir'}
          </button>
          {editando && (
            <button type="button" className="btn secondary"
                    onClick={() => { setEditando(null); setForm(vacia) }}>Cancelar</button>
          )}
        </div>
        <div className="field-hint">
          Marcada como secreta, su valor no se vuelve a mostrar: solo su longitud, que es lo que
          delata un valor mal copiado. Reservadas por la plataforma:{' '}
          {q.data.reservadas.map((r) => <code key={r} style={{ marginRight: 6 }}>{r}</code>)}
        </div>
      </form>

      {/* ── listado ───────────────────────────────────────────────────────── */}
      {q.data.variables.length === 0 ? (
        <Empty title="Sin variables propias">
          La aplicación recibe de momento solo las que gestiona la plataforma (abajo).
        </Empty>
      ) : (
        <div className="card"><div className="table-scroll">
          <table className="t">
            <thead><tr><th>Nombre</th><th>Valor</th><th>Última edición</th><th /></tr></thead>
            <tbody>
              {q.data.variables.map((v) => (
                <tr key={v.clave}>
                  <td style={{ fontFamily: 'var(--mono)' }}>{v.clave}</td>
                  <td style={{ fontFamily: 'var(--mono)', color: v.secreto ? 'var(--fg-3)' : 'var(--fg)' }}>
                    {v.ilegible
                      ? <span style={{ color: 'var(--danger)' }}>ilegible — vuelve a guardarla o bórrala</span>
                      : v.secreto ? `•••••• (${v.longitud} caracteres)` : (v.valor === '' ? <span style={{ color: 'var(--fg-3)' }}>(vacía)</span> : v.valor)}
                  </td>
                  <td style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                    {relTime(v.actualizadoAt)}{v.actualizadoPor ? ` · ${v.actualizadoPor}` : ''}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn sm secondary" style={{ marginRight: 6 }}
                            onClick={() => { setEditando(v.clave); setForm({ clave: v.clave, valor: '', secreto: v.secreto }) }}>
                      Cambiar
                    </button>
                    <button className="btn sm danger" onClick={() => setABorrar(v.clave)}>Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}

      {/* ── importar .env ─────────────────────────────────────────────────── */}
      <div className="section" style={{ marginTop: 28 }}>
        <h2 className="h2">Importar un <span className="mono">.env</span></h2>
        <p className="lede">
          Arrastra el fichero o pega su contenido. No se aplica nada hasta que revises el resumen.
        </p>
        <textarea
          className="input wide" rows={7} value={texto} spellCheck="false"
          style={{ fontFamily: 'var(--mono)', fontSize: 13, resize: 'vertical' }}
          placeholder={'API_KEY=sk-...\nSERVICIO_URL="https://ejemplo/x"'}
          onChange={(e) => { setTexto(e.target.value); previsualizar.reset() }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={async (e) => {
            e.preventDefault()
            const f = e.dataTransfer.files?.[0]
            if (f) { setTexto(await f.text()); previsualizar.reset() }
          }}
        />
        <div className="toolbar" style={{ marginTop: 10 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--fg-2)' }}>
            <input type="checkbox" checked={importarSecreto}
                   onChange={(e) => setImportarSecreto(e.target.checked)} />
            marcarlas como secretas
          </label>
          <button className="btn secondary" disabled={!texto.trim() || previsualizar.isPending}
                  onClick={() => previsualizar.mutate()}>
            {previsualizar.isPending ? 'Revisando…' : 'Revisar'}
          </button>
          {previa && (
            <button className="btn primary" disabled={importar.isPending}
                    onClick={() => importar.mutate()}>
              {importar.isPending ? 'Importando…' : `Importar ${previa.nuevas.length + previa.cambian.length}`}
            </button>
          )}
        </div>

        {previa && <Resumen previa={previa} />}
        {importar.isSuccess && (
          <div className="alert" style={{ marginTop: 12 }}>
            {importar.data.importadas.length} importadas. Pulsa <strong>Aplicar y desplegar</strong>{' '}
            para que la aplicación las reciba.
          </div>
        )}
      </div>


      {/* ── las que pone la plataforma ────────────────────────────────────── */}
      {q.data.plataforma?.length > 0 && (
        <div className="section" style={{ marginTop: 28 }}>
          <h2 className="h2">Variables de la plataforma</h2>
          <p className="lede">
            Las gestiona la consola y se inyectan en cada despliegue: no se pueden editar ni
            borrar desde aquí. Las <span className="mono">S3_*</span> aparecen al crear el
            almacenamiento del proyecto.
          </p>
          <div className="card"><div className="table-scroll">
            <table className="t">
              <thead><tr><th>Nombre</th><th>Valor</th></tr></thead>
              <tbody>
                {q.data.plataforma.map((v) => (
                  <tr key={v.clave}>
                    <td style={{ fontFamily: 'var(--mono)' }}>{v.clave}</td>
                    <td style={{ fontFamily: 'var(--mono)', color: v.secreto ? 'var(--fg-3)' : 'var(--fg)' }}>
                      {v.secreto ? `•••••• (${v.longitud} caracteres)` : v.valor}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></div>
        </div>
      )}

      <ConfirmarBorrado
        abierto={!!aBorrar} nombre={aBorrar ?? ''} titulo="Borrar la variable"
        ocupado={borrar.isPending}
        onCancelar={() => setABorrar(null)}
        onConfirmar={() => borrar.mutate(aBorrar)}
      >
        <p style={{ marginTop: 0 }}>
          Se quita <span className="mono">{aBorrar}</span> de la configuración del proyecto.
        </p>
        <p style={{ marginBottom: 0 }}>
          La aplicación dejará de recibirla <strong>en el siguiente despliegue</strong>. Si depende
          de ella, fallará al arrancar.
        </p>
      </ConfirmarBorrado>
    </>
  )
}

/** Qué va a pasar exactamente, antes de tocar nada. */
function Resumen({ previa }) {
  const { nuevas, cambian, iguales, rechazadas, repetidas } = previa
  const nada = !nuevas.length && !cambian.length
  return (
    <div className="card card-pad" style={{ marginTop: 12 }}>
      <strong>{nada ? 'No hay nada que importar' : 'Esto es lo que se va a hacer'}</strong>
      <ul style={{ margin: '10px 0 0 18px', fontSize: 13, lineHeight: 1.7 }}>
        {nuevas.length > 0 && (
          <li><strong style={{ color: 'var(--success)' }}>{nuevas.length} nuevas</strong>:{' '}
            <span className="mono">{nuevas.map((v) => v.clave).join(', ')}</span></li>
        )}
        {cambian.map((v) => (
          <li key={v.clave}>
            <strong style={{ color: 'var(--warning)' }}>se sobrescribe</strong>{' '}
            <span className="mono">{v.clave}</span> — valor actual:{' '}
            <span className="mono" style={{ color: 'var(--fg-3)' }}>{v.antes || '(vacío)'}</span>
          </li>
        ))}
        {iguales.length > 0 && <li style={{ color: 'var(--fg-3)' }}>{iguales.length} ya tenían ese mismo valor</li>}
        {repetidas.length > 0 && (
          <li style={{ color: 'var(--warning)' }}>
            repetidas en el fichero, gana la última:{' '}
            <span className="mono">{repetidas.join(', ')}</span>
          </li>
        )}
        {rechazadas.map((r, i) => (
          <li key={i} style={{ color: 'var(--danger)' }}>
            se ignora <span className="mono">{r.clave || `línea ${r.linea}`}</span> — {r.motivo}
          </li>
        ))}
      </ul>
    </div>
  )
}
